import {Lang, Language} from 'yougile-lang'
import {notDefined, Time, Util} from 'yougile-util'
import {ChatMessageJSON, ChatMessageWithPropertiesJSON} from './chat-message'
import {DataObjectId} from './data-object'
import {GTDStage} from './gtd'
import {
  getNotificationSetting,
  NotificationSettings,
  TaskNotificationAction
} from './notification-settings'
import {getMessageAssignIds} from './sticker'
import {UserAccount} from './user-account'

export interface SystemMessageData {
  type: SystemMessageType
  actionBy: DataObjectId
  value: string
}

export type SystemMessageType =
  | 'deadline'
  | 'deadlineNotice'
  | 'assigned'
  | 'gtd'
  | 'move'
  | 'none'

export class SystemMessageUtils {
  public static getData(
    message: ChatMessageJSON
  ): SystemMessageData | undefined {
    if (
      notDefined(message?.properties) ||
      !SystemMessageUtils.isKnownSystemMessage(message)
    ) {
      return undefined
    }
    const type = SystemMessageUtils.getType(message)
    if (type === 'none') {
      return undefined
    }
    return {
      actionBy: message.properties.actionBy as DataObjectId,
      type,
      value: SystemMessageUtils.getValue(message)
    }
  }

  public static getType(message: ChatMessageJSON): SystemMessageType {
    if (!SystemMessageUtils.isSystemMessage(message)) {
      return 'none'
    } else if (SystemMessageUtils.isDeadline(message)) {
      return 'deadline'
    } else if (SystemMessageUtils.isDeadlineNotice(message)) {
      return 'deadlineNotice'
    } else if (SystemMessageUtils.isAssignment(message)) {
      return 'assigned'
    } else if (SystemMessageUtils.isGtd(message)) {
      return 'gtd'
    } else if (SystemMessageUtils.isMove(message)) {
      return 'move'
    } else {
      return 'none'
    }
  }

  public static getValue(message: ChatMessageJSON): string {
    if (!message || !message.properties) {
      return ''
    }
    if (message.properties.deadlineRemained) {
      return message.properties.deadlineRemained + ''
    } else if (message.properties.assigned) {
      return message.properties.assigned + ''
    } else {
      return ''
    }
  }

  public static isLazyPlaceholder(message: ChatMessageJSON): boolean {
    return !!message?.properties?.isLazyPlaceholder
  }

  public static isSystemMessage(
    message: ChatMessageWithPropertiesJSON
  ): boolean {
    return !!message?.properties?.fromSystem
  }

  public static isKnownSystemMessage(
    message: ChatMessageWithPropertiesJSON
  ): boolean {
    return (
      SystemMessageUtils.isSystemMessage(message) &&
      (!!message.properties?.deadline ||
        !!message.properties?.deadlineNotice ||
        !!message.properties?.assigned ||
        !!message.properties?.move ||
        !!message.properties?.gtd)
    )
  }

  public static isMessageShouldNotify = (
    userId: DataObjectId,
    assigned: DataObjectId[] = [],
    settings: NotificationSettings,
    message: ChatMessageJSON
  ): boolean => {
    const check = (action: TaskNotificationAction) =>
      getNotificationSetting(settings, action, 'chat')

    const actionBy = message?.properties?.actionBy

    if (SystemMessageUtils.isSystemMessage(message)) {
      if (SystemMessageUtils.isKnownSystemMessage(message)) {
        if (
          SystemMessageUtils.isDeadlineNotice(message) ||
          SystemMessageUtils.isDeadline(message)
        ) {
          return assigned.includes(userId) && check('deadline')
        } else if (
          SystemMessageUtils.isAssignment(message) &&
          actionBy !== userId
        ) {
          const assigned = getMessageAssignIds(message)
          const hasSelf = assigned.includes(userId)
          const action = hasSelf ? 'selfAssigned' : 'otherAssigned'
          return check(action)
        } else if (SystemMessageUtils.isGtd(message) && actionBy !== userId) {
          return check('gtd')
        } else if (SystemMessageUtils.isMove(message) && actionBy !== userId) {
          return check('move')
        }
      }
      return false
    } else {
      return true
    }
  }

  public static isDeadlineNotice(message: ChatMessageJSON): boolean {
    return (
      SystemMessageUtils.isSystemMessage(message) &&
      !!message.properties?.deadlineNotice
    )
  }

  public static isDeadline(message: ChatMessageJSON): boolean {
    return (
      SystemMessageUtils.isSystemMessage(message) &&
      !!message.properties?.deadline
    )
  }

  public static isAssignment(message: ChatMessageJSON): boolean {
    return (
      SystemMessageUtils.isSystemMessage(message) &&
      !!message.properties?.assigned
    )
  }

  public static isMove(message: ChatMessageJSON): boolean {
    return (
      SystemMessageUtils.isSystemMessage(message) && !!message.properties?.move
    )
  }

  public static isGtd(message: ChatMessageJSON): boolean {
    return (
      SystemMessageUtils.isSystemMessage(message) && !!message.properties?.gtd
    )
  }

  public static deadlineNoticeText(
    message: ChatMessageJSON,
    language?: Language
  ): string {
    Util.assert(SystemMessageUtils.isDeadlineNotice(message), 'Invalid message')
    if (!message.properties) {
      return ''
    }

    const millis = +message.properties.deadlineRemained
    const minutes = Math.ceil(millis / Time.minute)
    const hours = minutes / 60

    const {deadlineNoticeDays, deadlineNoticeHours, deadlineNoticeMinutes} =
      language ? Lang.getMessages(language) : Lang.msg

    if (minutes < 60) {
      return deadlineNoticeMinutes(minutes)
    } else {
      return hours % 24 === 0
        ? deadlineNoticeDays(hours / 24)
        : deadlineNoticeHours(hours)
    }
  }

  public static deadlineText(
    message: ChatMessageJSON,
    language?: Language
  ): string {
    Util.assert(SystemMessageUtils.isDeadline(message), 'Invalid message')

    const {deadlineAlert} = language ? Lang.getMessages(language) : Lang.msg

    return deadlineAlert
  }

  public static assignmentText(
    message: ChatMessageJSON,
    assigned: string[],
    assignedBy: string,
    language?: Language
  ): string {
    Util.assert(SystemMessageUtils.isAssignment(message), 'Invalid message')
    const lang = language ? Lang.getMessages(language) : Lang.msg
    return lang.chat.assignInfo(assigned, assignedBy)
  }

  public static gtdText(
    message: ChatMessageJSON,
    who: string,
    done: boolean,
    language?: Language
  ): string {
    Util.assert(SystemMessageUtils.isGtd(message), 'Invalid message')
    const lang = language ? Lang.getMessages(language) : Lang.msg
    return lang.chat.gtdInfo(who, done)
  }

  public static moveText(
    message: ChatMessageJSON,
    who: string,
    from: string,
    to: string,
    language?: Language
  ): string {
    Util.assert(SystemMessageUtils.isMove(message), 'Invalid message')
    const lang = language ? Lang.getMessages(language) : Lang.msg
    return lang.chat.moveInfo(who, from, to)
  }

  public static systemMessageText(
    message: ChatMessageJSON,
    language?: Language,
    actionByAcc?: UserAccount
  ): string {
    const {
      deadlineNoticeText,
      deadlineText,
      isDeadlineNotice,
      isDeadline,
      isGtd,
      gtdText,
      isMove,
      moveText
    } = SystemMessageUtils

    if (isDeadlineNotice(message)) {
      return deadlineNoticeText(message, language)
    }

    if (isDeadline(message)) {
      return deadlineText(message, language)
    }

    if (isGtd(message)) {
      const after = message.properties?.after as GTDStage | undefined
      return gtdText(
        message,
        actionByAcc?.realName || '',
        after === 'done',
        language
      )
    }

    if (isMove(message)) {
      const from =
        message.properties?.fromTitle || message.properties?.from || ''
      const to = message.properties?.toTitle || message.properties?.to || ''

      return moveText(
        message,
        actionByAcc?.realName || '',
        typeof from === 'string' ? from : '',
        typeof to === 'string' ? to : '',
        language
      )
    }

    return message.text
  }

  private static isSupportedByServer(message: ChatMessageJSON): boolean {
    if (SystemMessageUtils.isSystemMessage(message)) {
      if (SystemMessageUtils.isKnownSystemMessage(message)) {
        return (
          SystemMessageUtils.isDeadline(message) ||
          SystemMessageUtils.isDeadlineNotice(message) ||
          SystemMessageUtils.isAssignment(message) ||
          SystemMessageUtils.isGtd(message) ||
          SystemMessageUtils.isMove(message)
        )
      } else {
        return false
      }
    } else {
      return true
    }
  }

  public static isMessageSupportedByMobile(message: ChatMessageJSON): boolean {
    if (SystemMessageUtils.isSystemMessage(message)) {
      if (SystemMessageUtils.isKnownSystemMessage(message)) {
        return (
          SystemMessageUtils.isDeadline(message) ||
          SystemMessageUtils.isDeadlineNotice(message) ||
          SystemMessageUtils.isAssignment(message)
        )
      } else {
        return false
      }
    } else {
      return true
    }
  }

  /**
   * Check if message is supported on mobile. Add there new message types after
   * they become supported on mobile.
   */
  public static isPushSupported(message: ChatMessageJSON): boolean {
    return SystemMessageUtils.isSupportedByServer(message)
  }

  public static isMailSupported(message: ChatMessageJSON): boolean {
    return SystemMessageUtils.isSupportedByServer(message)
  }
}
